import { Box, Stack, Typography } from '@mui/material'
import SectionContainer from './SectionContainer'
import { SectionTemplate } from '../types'
import { PATH_COLORS } from '../palette'

interface Props {
  template: SectionTemplate
  values: { primary?: string; secondary?: string; avoid?: string; rationale?: string; strategy?: string }
  title: string
  description: string
  sectionNumber?: number
}

function Pill({ label, value, color, large }: { label: string; value?: string; color: string; large?: boolean }) {
  if (!value) return null
  return (
    <Stack spacing={0.5}>
      <Typography
        variant="overline"
        sx={{ letterSpacing: 2, color, fontSize: 10, fontWeight: 700 }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: 'var(--ns-display-font)',
          fontSize: large ? { xs: 28, md: 38 } : { xs: 18, md: 22 },
          fontWeight: 500,
          lineHeight: 1.15,
          color: large ? '#1a1a1a' : '#3a3a3a',
        }}
      >
        {value}
      </Typography>
    </Stack>
  )
}

export default function DecisionSection({ template, values, title, description, sectionNumber }: Props) {
  const PRIMARY = PATH_COLORS[0]
  const SECONDARY = PATH_COLORS[1]
  const AVOID = '#a3a3a3'

  return (
    <SectionContainer
      id={template.sectionTypeId}
      number={sectionNumber}
      title={title}
      description={description}
      accentColor={PRIMARY}
    >
      <Stack spacing={4}>
        {/* Primary recommendation card with strong color emphasis */}
        <Box
          sx={{
            position: 'relative',
            p: { xs: 4, md: 6 },
            background: `linear-gradient(135deg, ${PRIMARY}14 0%, ${PRIMARY}06 100%)`,
            border: `2px solid ${PRIMARY}`,
            overflow: 'hidden',
          }}
        >
          {/* Decorative corner accent */}
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: 96,
              height: 96,
              background: PRIMARY,
              clipPath: 'polygon(100% 0, 0 0, 100% 100%)',
              opacity: 0.12,
            }}
          />
          <Pill label="Primary Recommendation" value={values?.primary} color={PRIMARY} large />
          {values?.rationale && (
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="overline"
                sx={{ letterSpacing: 2, fontSize: 10, color: PRIMARY, fontWeight: 700 }}
              >
                Rationale
              </Typography>
              <Typography variant="body1" sx={{ mt: 0.5, lineHeight: 1.6 }}>
                {values.rationale}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Secondary + Avoid as a 2-column row */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: 3,
          }}
        >
          <Box sx={{ p: 3, background: 'rgba(255,255,255,0.7)', borderLeft: `4px solid ${SECONDARY}`, borderTop: 1, borderRight: 1, borderBottom: 1, borderColor: 'divider' }}>
            <Pill label="Secondary Path" value={values?.secondary} color={SECONDARY} />
          </Box>
          <Box sx={{ p: 3, background: 'rgba(255,255,255,0.7)', borderLeft: `4px solid ${AVOID}`, borderTop: 1, borderRight: 1, borderBottom: 1, borderColor: 'divider' }}>
            <Pill label="Avoid as Lead Positioning" value={values?.avoid} color={AVOID} />
          </Box>
        </Box>

        {values?.strategy && (
          <Box>
            <Typography
              variant="overline"
              sx={{ letterSpacing: 2, fontSize: 10, color: PRIMARY, fontWeight: 700 }}
            >
              Strategy
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mt: 0.5,
                lineHeight: 1.7,
                fontSize: { xs: 16, md: 17 },
                fontStyle: 'italic',
                fontFamily: 'var(--ns-display-font)',
                color: '#3a3a3a',
              }}
            >
              {values.strategy}
            </Typography>
          </Box>
        )}
      </Stack>
    </SectionContainer>
  )
}
